import { ApolloClient } from '@apollo/client/core'
import { ApolloClients, provideApolloClient } from '@vue/apollo-composable'
import { boot } from 'quasar/wrappers'
import { getClientOptions } from 'src/apollo'
import auth from 'src/apollo/auth'
import siteInfo from 'src/apollo/queries/siteInfo.gql'
import groupImages from 'src/apollo/queries/getGroupImages.gql'
import { loadDevMessages } from '@apollo/client/dev'
import { getConfig } from 'src/configs'
import { initCDA } from 'src/core/hooks/init-cda'
import { consoleLogAPI, openNewMirror } from 'src/common/functions'
import { useSiteInfoStore } from 'src/stores/site-info'
import { useSystemStore } from 'src/stores/system'
import { useUserAuthStore } from 'src/stores/user/auth'
import { useGamesStore } from 'src/stores/games'
import { useServerStatusStore } from 'src/stores/server-status'
import { ApiServiceMain } from 'boot/api'
import { RESTRICTION } from 'src/common/dictionary'

const config = getConfig()

function getGroupImages(apolloClient) {
  return apolloClient
    .query({
      query: groupImages,
    })
    .then(({ data }) => {
      if (!data.gameGroupList?.length) {
        return
      }

      useGamesStore().setGroupListWithImages(data.gameGroupList)
    })
    .catch(consoleLogAPI)
}

function getSiteInfo(apolloClient) {
  return apolloClient
    .query({
      query: siteInfo,
    })
    .then(async ({ data }) => {
      const isBot = useSystemStore().isBot
      if (isBot) {
        useSiteInfoStore().updateSiteInfo(data.siteInfo)
        useServerStatusStore().updateUnderDev(data.siteInfo.underDev)
        return
      }

      if (data.siteInfo.additionalRedirect) {
        location.replace(data.siteInfo.additionalRedirect)
        return
      }

      const { domain, mirrorDomain } = data.siteInfo
      const isWebView = process.env.WEBVIEW === '1'
      const hasToken = useUserAuthStore().getToken
      const isNewDomain = domain !== mirrorDomain && Boolean(mirrorDomain.length)
      const isProduction = process.env.NODE_ENV === 'production' && process.env.ENVIRONMENT === 'production'

      if (isProduction && config.useMirrors && !isBot && !isWebView && hasToken && isNewDomain) {
        await openNewMirror(mirrorDomain, apolloClient)
      }
      useSiteInfoStore().updateSiteInfo(data.siteInfo)
      useServerStatusStore().updateUnderDev(data.siteInfo.underDev)
    })
    .catch((e) => {
      consoleLogAPI(e)
      if (e?.networkError?.statusCode >= 500) {
        useServerStatusStore().updateUnderDev(true)
      }
    })
}

export default boot(async (ctx) => {
  const { app } = ctx
  const options = getClientOptions()
  const apolloClient = new ApolloClient(options)

  const serverStatusStore = useServerStatusStore()

  const apolloClients = {
    default: apolloClient,
  }

  app.config.globalProperties.$apollo = apolloClient

  const systemStore = useSystemStore()
  const isBot = systemStore.isBot

  provideApolloClient(apolloClient)
  app.provide(ApolloClients, apolloClients)

  if (process.env.NODE_ENV === 'development') {
    loadDevMessages()
  }

  if (isBot) {
    serverStatusStore.updateRestricted(RESTRICTION.SERVICE_ALLOWED)
    await getSiteInfo(apolloClient)
    return
  }

  await ApiServiceMain.common
    .restrictions()
    .then(({ data }) => {
      serverStatusStore.updateRestricted(data.restricted)
    })
    .catch(() => {
      serverStatusStore.updateRestricted(RESTRICTION.SERVICE_ALLOWED)
    })

  if (serverStatusStore.restricted === RESTRICTION.SERVICE_NOT_ALLOWED) {
    return
  }

  getGroupImages(apolloClient)
  await Promise.allSettled([auth(apolloClient), getSiteInfo(apolloClient)]).then((data) => {
    const authResponse = data[0]
    if (authResponse.status === 'rejected') {
      serverStatusStore.updateServerIsOk(false)
    }
  })

  if (!serverStatusStore.serverIsOk) {
    return
  }
  await initCDA(apolloClient)
})
